/**
 * generates a random hash using Math.random()
 *
 * @returns {String}
 */
export const randomHash = () => `${Math.random().toString(36).substring(2, 15)}`

/**
 * creates hex string of the ArrayBuffer
 *
 * takes an ArrayBuffer and then converts it into Uint8Array
 * converts each element of Uint8Array and converts the value into Hex
 * pads the hex value with 0 to make it 2 character strings
 * returns the joined Array
 *
 * @param {ArrayBuffer} buffer
 * @returns {String}
 */
export const paddedHexString = (buffer) => {
  const byteArray = new Uint8Array(buffer)

  const hexCodes = Array.from(byteArray).map((value) =>
    value.toString(16).padStart(2, "0")
  )

  return hexCodes.join("")
}

/**
 * creates a sha256 of the given string
 *
 * based on the runtime environment, creates a sha256 hash of the string
 * before converting to hex
 *
 * @param {String} input
 * @returns {Promise}
 */
export const sha256 = async (input) => {
  if (process.browser) {
    if (!window.crypto.subtle) {
      return Promise.reject(
        new Error("`crypto` only works in secure contexts.")
      )
    }
    const buffer = await window.crypto.subtle.digest("SHA-256", input)
    return paddedHexString(buffer)
    /* eslint-disable-next-line no-else-return */
  } else {
    // this kind of `process.browser` diversification is added because
    // node doesn't support the WebCrypto API yet. node 16 has experimental
    // support for the WebCrypto API.
    // once support is mature, it would be wise & safe to remove `process.browser`
    // also would need to update the rollup config & package metadata accordingly
    /**
     * @see https://nodejs.org/dist/latest-v16.x/docs/api/webcrypto.html
     */
    const { createHash } = require("crypto")
    const hash = createHash("sha256")
    hash.update(input)
    return hash.digest("hex")
  }
}

/**
 * creates a sha256 of the given string
 *
 * creates a sha256 hash as hex value in browser or node
 *
 * @param {String} data
 * @returns {Promise}
 */
export const hash = (data) => {
  const encoder = new TextEncoder("utf-8")
  return sha256(encoder.encode(data))
}
